import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export default function Profil() {
  const { id } = useParams();
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  const isMoi = !id || parseInt(id) === user?.id;

  const [profil, setProfil] = useState(null);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saveMsg, setSaveMsg] = useState('');

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const cible = id || 'me';
    Promise.all([
      api.get(`/users/${cible}`),
      isMoi ? api.get('/moments/mes') : Promise.resolve({ data: [] }),
    ])
      .then(([rProfil, rMoments]) => {
        setProfil(rProfil.data);
        setForm({
          prenom: rProfil.data.prenom,
          ville: rProfil.data.ville,
          bio: rProfil.data.bio || '',
        });
        setMoments(rMoments.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, isMoi]);

  // Sauvegarder les infos du profil
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.put('/users/me', form);
      updateUser(form);
      setProfil(p => ({ ...p, ...form }));
      setEditMode(false);
      setSaveMsg('Profil mis à jour ✓');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch {}
  };

  // Sélectionner et uploader une photo
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Aperçu local immédiat
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload vers Cloudinary via le backend
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await api.put('/upload/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser({
  avatar_url: res.data.avatar_url
});
      const newUrl = res.data.avatar_url;
      updateUser({ avatar_url: newUrl });
      setProfil(p => ({ ...p, avatar_url: newUrl }));
      setAvatarPreview(null); // utiliser l'URL Cloudinary désormais
      setSaveMsg('Photo mise à jour ✓');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch {
      setSaveMsg('Erreur lors de l\'upload.');
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
    }
  };

  if (loading) {
    return (
      <main className="k-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="k-spinner" />
      </main>
    );
  }

  if (!profil) {
    return (
      <main className="k-page" style={{ padding: 40 }}>
        <p>Profil introuvable.</p>
      </main>
    );
  }

  const avatarSrc = avatarPreview || profil.avatar_url;
  const initiale = profil.prenom?.[0]?.toUpperCase();

  return (
    <main className="k-page">
      <div style={{ padding: '28px 20px 48px' }}>

        {/* ===== CARTE HERO ===== */}
        <div style={S.heroCard} className="fade-up">

          {/* Avatar cliquable si c'est mon profil */}
          <div style={S.avatarWrap}>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={profil.prenom}
                style={S.avatarImg}
              />
            ) : (
              <div style={S.avatarInitiale}>{initiale}</div>
            )}

            {isMoi && (
              <>
                <button
                  style={S.avatarEditBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  title="Changer la photo"
                >
                  {avatarUploading ? (
                    <div className="k-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  ) : (
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                      photo_camera
                    </span>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                />
              </>
            )}
          </div>

          <div style={S.profileName}>{profil.prenom}</div>

          {profil.ville && (
            <div style={S.location}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>location_on</span>
              {profil.ville}
              {profil.distance_km && ` · À ${profil.distance_km} km`}
            </div>
          )}

          {saveMsg && <div style={S.saveMsg}>{saveMsg}</div>}
        </div>

        {/* ===== FORMULAIRE D'ÉDITION ===== */}
        {isMoi && editMode ? (
          <form onSubmit={handleSave} style={{ marginTop: 20 }}>
            <div style={{ marginBottom: 16 }}>
              <label className="k-label">Prénom</label>
              <input
                className="k-input"
                value={form.prenom}
                onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                required
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="k-label">Ville</label>
              <input
                className="k-input"
                value={form.ville}
                onChange={e => setForm(f => ({ ...f, ville: e.target.value }))}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label className="k-label">Ma présentation</label>
              <textarea
                className="k-input"
                value={form.bio}
                onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                placeholder="Quelques mots sur ce que vous aimez partager…"
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="k-button" style={{ flex: 1 }}>
                Enregistrer
              </button>
              <button
                type="button"
                className="k-button-soft"
                style={{ flex: 1 }}
                onClick={() => setEditMode(false)}
              >
                Annuler
              </button>
            </div>
          </form>
        ) : (
          <>
            {/* ===== À PROPOS ===== */}
            <div style={S.section}>
              <div className="k-section-title">À propos</div>
              <div style={S.textBox}>
                {profil.bio || (
                  <em style={{ color: 'var(--c-text-3)' }}>
                    {isMoi ? 'Ajoutez une présentation pour vous faire connaître.' : 'Aucune présentation.'}
                  </em>
                )}
              </div>
            </div>

            {/* ===== MES MOMENTS (seulement si mon profil) ===== */}
            {isMoi && moments.length > 0 && (
              <div style={S.section}>
                <div className="k-section-title">Mes moments proposés</div>
                <div style={S.textBox}>
                  {moments.map(m => (
                    <div key={m.id} style={S.momentRow}>
                      <div style={S.momentIcon}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                          directions_walk
                        </span>
                      </div>
                      <div style={{ flex: 1, fontFamily: 'Manrope,sans-serif', fontSize: 14, fontWeight: 600 }}>
                        {m.titre}
                      </div>
                      <div style={{ fontFamily: 'Manrope,sans-serif', fontSize: 11, color: 'var(--c-text-3)', flexShrink: 0 }}>
                        {m.date_moment
                          ? new Date(m.date_moment).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                          : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ===== ACTIONS ===== */}
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {isMoi ? (
                <>
                  <button className="k-button" onClick={() => setEditMode(true)}>
                    Modifier mon profil
                  </button>
                  <button
                    className="k-button-soft"
                    onClick={() => { logout(); navigate('/'); }}
                  >
                    Se déconnecter
                  </button>
                </>
              ) : (
                <>
                  <div style={S.safety}>
                    <span className="material-symbols-outlined" style={{ color: 'var(--c-primary)', fontSize: 18 }}>
                      shield
                    </span>
                    <p style={{ fontFamily: 'Manrope,sans-serif', fontSize: 12, color: 'var(--c-primary)', fontWeight: 600 }}>
                      Rencontrez-vous toujours dans des lieux publics.
                    </p>
                  </div>
                  <button className="k-button" onClick={() => navigate(`/messages/${profil.id}`)}>
                    Dire bonjour
                  </button>
                  <button className="k-button-soft" onClick={() => navigate('/moment')}>
                    Proposer un moment
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm('Signaler ce profil à l\'équipe Kindeo ?')) {
                        await api.post(`/messages/signaler/${id}`, { raison: 'Signalement depuis le profil' });
                        alert('Signalement enregistré. Merci.');
                      }
                    }}
                    style={{ background: 'none', border: 'none', color: 'var(--c-accent)', fontFamily: 'Manrope,sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 8, textAlign: 'center' }}
                  >
                    Signaler ce profil
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}

const S = {
  heroCard: {
    textAlign: 'center',
    padding: '28px 20px 24px',
    background: 'var(--c-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--c-border)',
    boxShadow: 'var(--shadow-sm)',
    marginBottom: 20,
  },
  avatarWrap: {
    position: 'relative',
    width: 88,
    height: 88,
    margin: '0 auto 16px',
  },
  avatarImg: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid var(--c-primary)',
    display: 'block',
  },
  avatarInitiale: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    background: 'var(--c-primary-lt)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Manrope,sans-serif',
    fontSize: 32,
    fontWeight: 800,
    color: 'var(--c-primary)',
    border: '3px solid var(--c-primary)',
  },
  avatarEditBtn: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'var(--c-primary)',
    color: '#fff',
    border: '2px solid #fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-sm)',
  },
  profileName: {
    fontFamily: 'Manrope,sans-serif',
    fontSize: 22,
    fontWeight: 800,
    color: 'var(--c-text)',
    letterSpacing: '-0.4px',
    marginBottom: 6,
  },
  location: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontFamily: 'Manrope,sans-serif',
    fontSize: 13,
    color: 'var(--c-primary)',
    fontWeight: 600,
  },
  saveMsg: {
    marginTop: 12,
    fontFamily: 'Manrope,sans-serif',
    fontSize: 13,
    color: 'var(--c-primary)',
    fontWeight: 600,
  },
  section: { marginTop: 20 },
  textBox: {
    background: 'var(--c-surface)',
    padding: 18,
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--c-border)',
    fontSize: 14,
    color: 'var(--c-text-2)',
    lineHeight: 1.65,
    boxShadow: 'var(--shadow-sm)',
  },
  momentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
    borderBottom: '1px solid var(--c-border)',
  },
  momentIcon: {
    width: 36,
    height: 36,
    borderRadius: 'var(--radius-md)',
    background: 'var(--c-primary-lt)',
    color: 'var(--c-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  safety: {
    background: 'var(--c-primary-lt)',
    borderRadius: 'var(--radius-md)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
};
